import { EventDispatcher } from '../events/EventDispatcher';
import { TimerEvent } from '../events/Event';
import { rt } from '../player/runtime';

let timerSeq = 0;

/**
 * flash.utils.Timer driven by the runtime clock. Due timers fire at the start of a player tick in
 * due-time order; a timer fires at most `MAX_CATCHUP` times per tick (Flash cannot fire faster
 * than it gets control back either).
 */
export class Timer extends EventDispatcher {
  /** @internal */ $delay: number;
  /** @internal */ $repeatCount: number;
  /** @internal */ $currentCount: number;
  /** @internal */ $running: boolean;
  /** @internal */ $next: number;
  /** @internal */ $seq: number;

  constructor(delay: number, repeatCount: number = 0) {
    super();
    delay = +delay;
    if (!(delay >= 0) || !Number.isFinite(delay)) throw new RangeError('Error #2066: The Timer delay specified is out of range.');
    this.$delay = delay;
    this.$repeatCount = Math.max(0, repeatCount | 0);
    this.$currentCount = 0;
    this.$running = false;
    this.$next = 0;
    this.$seq = ++timerSeq;
  }

  get delay(): number {
    return this.$delay;
  }
  set delay(v: number) {
    v = +v;
    if (!(v >= 0) || !Number.isFinite(v)) throw new RangeError('Error #2066: The Timer delay specified is out of range.');
    this.$delay = v;
    if (this.$running) this.$next = rt().now() + Math.max(1, v);
  }
  get repeatCount(): number {
    return this.$repeatCount;
  }
  set repeatCount(v: number) {
    this.$repeatCount = Math.max(0, v | 0);
    if (this.$repeatCount > 0 && this.$currentCount >= this.$repeatCount) this.stop();
  }
  get currentCount(): number {
    return this.$currentCount;
  }
  get running(): boolean {
    return this.$running;
  }

  start(): void {
    if (this.$running) return;
    if (this.$repeatCount > 0 && this.$currentCount >= this.$repeatCount) return;
    this.$running = true;
    this.$next = rt().now() + Math.max(1, this.$delay);
    rt().timers.add(this);
  }

  stop(): void {
    this.$running = false;
    rt().timers.delete(this);
  }

  reset(): void {
    this.stop();
    this.$currentCount = 0;
  }

  /** @internal */ $fire(): void {
    this.$currentCount++;
    this.$dispatch(new TimerEvent(TimerEvent.TIMER, false, false));
    if (this.$repeatCount > 0 && this.$currentCount >= this.$repeatCount) {
      this.stop();
      this.$dispatch(new TimerEvent(TimerEvent.TIMER_COMPLETE, false, false));
    }
  }
}

const MAX_CATCHUP = 10;

/** @internal Fire due timers (called by FlashPlayer.tick after advancing the clock). */
export function processTimers(now: number): void {
  const r = rt();
  const fired = new Map<Timer, number>();
  for (let guard = 0; guard < 10000; guard++) {
    let best: Timer | null = null;
    for (const t of r.timers as Set<Timer>) {
      if (!t.$running || t.$next > now) continue;
      if ((fired.get(t) ?? 0) >= MAX_CATCHUP) continue;
      if (!best || t.$next < best.$next || (t.$next === best.$next && t.$seq < best.$seq)) best = t;
    }
    if (!best) break;
    fired.set(best, (fired.get(best) ?? 0) + 1);
    best.$next += Math.max(1, best.$delay);
    best.$fire();
  }
  for (const [t, n] of fired) if (n >= MAX_CATCHUP && t.$running && t.$next <= now) t.$next = now + Math.max(1, t.$delay);
}

/** Milliseconds since runtime start (driven by the platform clock; deterministic headless). */
export function getTimer(): number {
  return rt().getTimer();
}
